import { useState } from "react";
import { Link } from "react-router-dom";
import { formatRelative, shortAddr } from "../../lib/api";
import { safeHttpsEndpoint, type X402RouteContext } from "../../lib/x402Discovery";
import { isSafeBrowserNavigationMethod } from "../../lib/x402Verification";

export function X402ContextPanel({ context }: { context: X402RouteContext }) {
  const [copied, setCopied] = useState(false);
  const [proofCopied, setProofCopied] = useState(false);
  const endpoint = safeHttpsEndpoint(context);
  const canOpen = endpoint !== null && isSafeBrowserNavigationMethod(context.note.route.method);
  const references = [context.note.requestSchemaUri, context.note.documentationUri, ...context.note.contextUris]
    .filter((value): value is string => Boolean(value));

  return (
    <section className="border border-faint bg-paper rounded-md overflow-hidden">
      <header className="p-5 md:p-6 border-b border-faint bg-paper-deep/55">
        <div className="flex flex-wrap items-center gap-2">
          <span className="pill pill-olive">participant-signed judgment</span>
          <span className="pill pill-faint">{context.participantRoles.join(" + ")}</span>
        </div>
        <h2 className="mt-4 text-2xl font-display tracking-tight">How this endpoint fit</h2>
        <p className="mt-2 text-sm text-muted leading-relaxed">
          {context.note.fit}
        </p>
        {context.signedPayload && (
          <button
            className="mt-4 btn-mono text-xs text-olive-deep hover:underline min-h-11"
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(JSON.stringify(context.signedPayload));
              setProofCopied(true);
            }}
          >
            {proofCopied ? "Signed payload copied" : "Copy exact signed payload"}
          </button>
        )}
      </header>
      <div className="p-5 md:p-6 space-y-6">
        <div>
          <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Endpoint address</p>
          <p className="mt-2 btn-mono text-sm break-all text-ink">{context.note.route.method} {context.note.route.publicEndpointUrl}</p>
          <p className="mt-2 text-xs text-warning">Review the host, method, request shape, and current price before use. Boon never calls it automatically.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="btn btn-ghost"
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(context.note.route.publicEndpointUrl);
                setCopied(true);
              }}
            >
              {copied ? "Endpoint copied" : "Copy endpoint"}
            </button>
            {canOpen && (
              <a className="btn btn-ghost" href={endpoint} target="_blank" rel="noopener noreferrer nofollow">
                Open GET endpoint ↗
              </a>
            )}
          </div>
          {!canOpen && (
            <p className="mt-2 text-xs text-muted">
              A browser link would change {context.note.route.method} to GET. Copy this URL into an x402-aware client that preserves the signed method.
            </p>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Fact label="Intended use" value={context.note.intent} />
          <IdentityFact context={context} />
          <Fact label="Note published" value={formatRelative(String(context.publishedAt))} />
          <Fact label="Network" value={context.offer.network} mono />
        </div>
        {context.note.limits.length > 0 && <Fact label="Declared limits" value={context.note.limits.join(" • ")} />}
        {references.length > 0 && (
          <div>
            <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Attached references</p>
            <ul className="mt-2 space-y-2">
              {references.map((value) => <li key={value} className="btn-mono text-xs break-all text-ink-soft">{value}</li>)}
            </ul>
            <p className="mt-2 text-xs text-muted">Displayed as inert text. Boon does not fetch IPFS, Arweave, or web attachments.</p>
          </div>
        )}
        <div className="border-t border-faint pt-5 text-xs text-muted leading-relaxed">
          The participant signature verifies these exact note bytes. It does not prove endpoint ownership, current availability, safety, or output quality. The endpoint was not fetched while rendering this page.
        </div>
      </div>
    </section>
  );
}

function IdentityFact({ context }: { context: X402RouteContext }) {
  return (
    <div>
      <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Participant</p>
      <div className="mt-1 text-sm leading-relaxed text-ink-soft">
        {context.publisherAccounts.length > 0 ? (
          <>
            {context.publisherAccounts.map((account, index) => (
              <span key={account.handle}>
                {index > 0 && ", "}
                <Link className="text-olive-deep hover:underline" to={`/p/${encodeURIComponent(account.handle)}`}>
                  {account.handle}
                </Link>
              </span>
            ))}
            <span className="text-muted"> · {shortAddr(context.publisher)}</span>
          </>
        ) : shortAddr(context.publisher)}
        <span className="block text-xs text-muted">signed as {context.participantRoles.join(" + ")}</span>
      </div>
    </div>
  );
}

function Fact({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">{label}</p>
      <p className={`mt-1 text-sm leading-relaxed break-words ${mono ? "btn-mono break-all" : "text-ink-soft"}`}>{value}</p>
    </div>
  );
}
